import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchJson } from "@/lib/api";
import type { PackageReturnReason, ReturnCancelStatus } from "@/lib/package-return-labels";

export type PackageReturnEntry = {
  id: string;
  trackingNumber: string;
  orderId: string | null;
  storeId: string | null;
  storeName: string | null;
  credentialId: string | null;
  credentialName: string | null;
  reason: PackageReturnReason;
  notes: string;
  codAmount: number | null;
  codCurrency: string | null;
  returnedAt: number;
  createdBy: string;
  createdAt: number;
  cancelStatus: ReturnCancelStatus | "";
  cancelError: string;
};

export type CancelOutcome = { status: ReturnCancelStatus; message: string };

export type SuspiciousPackage = {
  codId: string;
  orderId: string;
  storeId: string;
  storeName: string;
  trackingNumber: string | null;
  credentialName: string | null;
  amount: number;
  currency: string;
  packedAt: number;
  ageDays: number;
};

function invalidateReturns(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ["package-returns"] });
  qc.invalidateQueries({ queryKey: ["package-returns-summary"] });
  qc.invalidateQueries({ queryKey: ["package-returns-suspicious"] });
  qc.invalidateQueries({ queryKey: ["cod-ledger"] });
  qc.invalidateQueries({ queryKey: ["cod-summary"] });
}

export function usePackageReturns(
  storeId: string | null,
  filters: { reason?: string; q?: string; after?: number; before?: number; page?: number },
) {
  const params = new URLSearchParams();
  if (storeId) params.set("storeId", storeId);
  if (filters.reason) params.set("reason", filters.reason);
  if (filters.q) params.set("q", filters.q);
  if (filters.after) params.set("after", String(filters.after));
  if (filters.before) params.set("before", String(filters.before));
  if (filters.page) params.set("page", String(filters.page));
  return useQuery({
    queryKey: ["package-returns", storeId ?? "all", filters],
    queryFn: () =>
      fetchJson<{ data: PackageReturnEntry[]; total: number; hasNextPage: boolean }>(
        `/api/package-returns?${params}`,
      ),
  });
}

export function usePackageReturnsSummary(storeId: string | null) {
  return useQuery({
    queryKey: ["package-returns-summary", storeId ?? "all"],
    queryFn: () =>
      fetchJson<{ total: number; byReason: Record<string, number> }>(
        storeId ? `/api/package-returns/summary?storeId=${storeId}` : "/api/package-returns/summary",
      ),
  });
}

export function useSuspiciousPackages(storeId: string | null, days: number) {
  const params = new URLSearchParams({ days: String(days) });
  if (storeId) params.set("storeId", storeId);
  return useQuery({
    queryKey: ["package-returns-suspicious", storeId ?? "all", days],
    queryFn: () => fetchJson<SuspiciousPackage[]>(`/api/package-returns/suspicious?${params}`),
  });
}

export function useCheckCustomerReturnsBulk(
  customers: { orderId: string; email?: string; phone?: string }[],
) {
  return useQuery({
    queryKey: ["package-returns-customer-check", customers.map((c) => c.orderId).join(",")],
    queryFn: () =>
      fetchJson<Record<string, { count: number; reasons: PackageReturnReason[] }>>(
        "/api/package-returns/check-customers",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ customers }),
        },
      ),
    enabled: customers.length > 0,
  });
}

export type ScanLookup = {
  trackingNumber: string;
  linked: boolean;
  orderId: string | null;
  storeId: string | null;
  storeName: string | null;
  credentialName: string | null;
  codAmount: number | null;
  codCurrency: string | null;
  existing: PackageReturnEntry | null;
  order: ScanOrderInfo | null;
};

export type ScanOrderInfo = {
  customerName: string | null;
  customerPhone: string | null;
  customerEmail: string | null;
  address: string | null;
  city: string | null;
  postcode: string | null;
  orderStatus: string | null;
  orderTotal: number | null;
  orderCurrency: string | null;
  orderDate: string | null;
  customerNote: string | null;
  items: { sku: string | null; name: string | null; quantity: number | null }[];
};

export function useLookupPackageReturn() {
  return useMutation({
    mutationFn: (code: string) =>
      fetchJson<ScanLookup>(`/api/package-returns/lookup?code=${encodeURIComponent(code)}`),
  });
}

export function useScanPackageReturn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { code: string; reason: PackageReturnReason; notes?: string }) =>
      fetchJson<{ entry: PackageReturnEntry; linked: boolean; cancel: CancelOutcome }>("/api/package-returns/scan", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => invalidateReturns(qc),
  });
}

export function useBulkScanPackageReturn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { codes: string[]; reason: PackageReturnReason }) =>
      fetchJson<{
        recorded: PackageReturnEntry[];
        unlinkedCodes: string[];
        duplicates: string[];
        cancelCounts: Partial<Record<ReturnCancelStatus, number>>;
      }>(
        "/api/package-returns/scan-bulk",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        },
      ),
    onSuccess: () => invalidateReturns(qc),
  });
}

export function useRecordOrderReturn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { orderId: string; storeId: string; reason: PackageReturnReason; notes?: string }) =>
      fetchJson<{ entry: PackageReturnEntry; linked: boolean; cancel: CancelOutcome }>("/api/package-returns/record-order", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => invalidateReturns(qc),
  });
}

/** Ručno (ponovno) otkazivanje narudžbe za već evidentiran povrat.
 *  `force` otkazuje i plaćenu narudžbu — povrat novca ide ručno u Woo. */
export function useCancelReturnOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, force }: { id: string; force?: boolean }) =>
      fetchJson<{ entry: PackageReturnEntry; cancel: CancelOutcome }>(
        `/api/package-returns/${id}/cancel-order`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ force: !!force }),
        },
      ),
    onSuccess: () => {
      invalidateReturns(qc);
      qc.invalidateQueries({ queryKey: ["orders"] });
    },
  });
}

export function useUpdatePackageReturn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; reason?: PackageReturnReason; notes?: string }) =>
      fetchJson<PackageReturnEntry>(`/api/package-returns/${id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => invalidateReturns(qc),
  });
}

export function useDeletePackageReturn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      fetchJson<{ deleted: boolean }>(`/api/package-returns/${id}`, { method: "DELETE" }),
    onSuccess: () => invalidateReturns(qc),
  });
}
